import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Save, X, Search, Database, Globe, Tag, Filter } from 'lucide-react'
import { sampleValuesSetAPI } from '../services/api'
import { categoryAPI } from '../services/api'
import CategoryDropdown from './CategoryDropdown'

const SampleValuesSetManagement = () => {
  const [sampleValuesSets, setSampleValuesSets] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingSampleValuesSet, setEditingSampleValuesSet] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    category_id: '',
    language: '',
    sample_set_code: ''
  })
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    language: '',
    sample_set_code: '',
    category_id: ''
  })
  const [errors, setErrors] = useState({})
  const [uniqueLanguages, setUniqueLanguages] = useState([])
  const [uniqueSampleSetCodes, setUniqueSampleSetCodes] = useState([])

  // Load data
  useEffect(() => {
    fetchSampleValuesSets()
    fetchCategories()
    fetchUniqueData()
  }, [])

  const fetchSampleValuesSets = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        search: searchTerm,
        ...filters
      }
      const response = await sampleValuesSetAPI.getAll(params)
      
      if (response.data.success) {
        setSampleValuesSets(response.data.data || [])
      } else {
        setError(response.data.message || 'Failed to fetch sample values sets')
      }
    } catch (error) {
      console.error('Error fetching sample values sets:', error)
      setError('Failed to load sample values sets')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll()
      if (response.data.success) {
        setCategories(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchUniqueData = async () => {
    try {
      const [languagesRes, sampleSetCodesRes] = await Promise.all([
        sampleValuesSetAPI.getUniqueLanguages(),
        sampleValuesSetAPI.getUniqueSampleSetCodes()
      ])
      
      if (languagesRes.data.success) {
        setUniqueLanguages(languagesRes.data.data || [])
      }
      if (sampleSetCodesRes.data.success) {
        setUniqueSampleSetCodes(sampleSetCodesRes.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching unique data:', error)
    }
  }

  // Search and filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSampleValuesSets()
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchTerm, filters])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      let response
      
      if (editingSampleValuesSet) {
        response = await sampleValuesSetAPI.update(editingSampleValuesSet.id, formData)
      } else {
        response = await sampleValuesSetAPI.create(formData)
      }
      
      if (response.data.success) {
        await fetchSampleValuesSets()
        resetForm()
        setShowCreateForm(false)
        setEditingSampleValuesSet(null)
      } else {
        setError(response.data.message || 'Failed to save sample values set')
      }
    } catch (error) {
      console.error('Error saving sample values set:', error)
      setError('Failed to save sample values set')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (sampleValuesSetId) => {
    if (!window.confirm('Are you sure you want to delete this sample values set?')) {
      return
    }
    
    try {
      setLoading(true)
      const response = await sampleValuesSetAPI.delete(sampleValuesSetId)
      
      if (response.data.success) {
        await fetchSampleValuesSets()
      } else {
        setError(response.data.message || 'Failed to delete sample values set')
      }
    } catch (error) {
      console.error('Error deleting sample values set:', error)
      setError('Failed to delete sample values set')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (sampleValuesSet) => {
    setEditingSampleValuesSet(sampleValuesSet)
    setFormData({
      key: sampleValuesSet.key || '',
      value: sampleValuesSet.value || '',
      language: sampleValuesSet.language || '',
      sample_set_code: sampleValuesSet.sample_set_code || '',
      category_id: sampleValuesSet.category_id || ''
    })
    setShowCreateForm(true)
  }

  const resetForm = () => {
    setFormData({
      key: '',
      value: '',
      language: '',
      sample_set_code: '',
      category_id: ''
    })
    setErrors({})
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required'
    }

    if (!formData.value.trim()) {
      newErrors.value = 'Value is required'
    }

    if (!formData.language.trim()) {
      newErrors.language = 'Language is required'
    }

    if (!formData.sample_set_code.trim()) {
      newErrors.sample_set_code = 'Sample set code is required'
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      category_id: '',
      language: '',
      sample_set_code: ''
    })
    setSearchTerm('')
  }

  if (loading && sampleValuesSets.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <Database className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Sample Values Set Management</h1>
        </div>
        <button
          onClick={() => {
            resetForm()
            setEditingSampleValuesSet(null)
            setShowCreateForm(true)
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          <span>Add Sample Values Set</span>
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search sample values sets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category_id}
              onChange={(e) => handleFilterChange('category_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            <select
              value={filters.language}
              onChange={(e) => handleFilterChange('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Languages</option>
              {uniqueLanguages.map(lang => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sample Set Code</label>
            <select
              value={filters.sample_set_code}
              onChange={(e) => handleFilterChange('sample_set_code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Sample Sets</option>
              {uniqueSampleSetCodes.map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              <Filter className="w-4 h-4" />
              <span>Clear Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">
            {editingSampleValuesSet ? 'Edit Sample Values Set' : 'Create New Sample Values Set'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key *
                </label>
                <input
                  type="text"
                  required
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Sample key"
                />
                {errors.key && <p className="text-sm text-red-600 mt-1">{errors.key}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language *
                </label>
                <input
                  type="text"
                  required
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="en, es, fr, etc."
                />
                {errors.language && <p className="text-sm text-red-600 mt-1">{errors.language}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sample Set Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.sample_set_code}
                  onChange={(e) => setFormData({ ...formData, sample_set_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="sample_set_001"
                />
                {errors.sample_set_code && <p className="text-sm text-red-600 mt-1">{errors.sample_set_code}</p>}
              </div>

              <CategoryDropdown
                value={formData.category_id}
                onChange={(value) => setFormData({ ...formData, category_id: value })}
                placeholder="Select Category"
                type="template"
                isActive={true}
                required={true}
                label="Category"
                error={errors.category_id}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value *
              </label>
              <textarea
                required
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                placeholder="Sample value content"
              />
              {errors.value && <p className="text-sm text-red-600 mt-1">{errors.value}</p>}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{editingSampleValuesSet ? 'Update' : 'Create'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setEditingSampleValuesSet(null)
                  resetForm()
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sample Values Sets List */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Sample Values Sets ({sampleValuesSets.length})</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {sampleValuesSets.length > 0 ? (
            sampleValuesSets.map((sampleValuesSet) => (
              <div key={sampleValuesSet.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Database className="w-6 h-6 text-blue-600" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-gray-900">{sampleValuesSet.key}</h4>
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {sampleValuesSet.language}
                        </span>
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                          {sampleValuesSet.sample_set_code}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{sampleValuesSet.category_name}</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{sampleValuesSet.value}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(sampleValuesSet)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
                      title="Edit Sample Values Set"
                    >
                      <Edit className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDelete(sampleValuesSet.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                      title="Delete Sample Values Set"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-12 text-center">
              <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sample values sets found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || Object.values(filters).some(f => f) 
                  ? 'No sample values sets match your search or filters.' 
                  : 'Get started by creating your first sample values set.'
                }
              </p>
              {!searchTerm && !Object.values(filters).some(f => f) && (
                <button
                  onClick={() => {
                    resetForm()
                    setEditingSampleValuesSet(null)
                    setShowCreateForm(true)
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Sample Values Set
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SampleValuesSetManagement
